/* components/patterns/chat/Markdown.jsx — лёгкий Markdown-рендер для ленты чата (S5).
   Поддержка: заголовки, **жирный**, *курсив*, `код`, код-блоки (с «Копировать»),
   списки (ul/ol), GFM-таблицы (обёртка .md-table-wrap — гор. скролл на xs),
   цитаты (>), ссылки (target=_blank), <hr>.
   ХЕНДОФФ: заменить на react-markdown + remark-gfm (контракт компонентов совпадает). */

import React from 'react';
import { Icon } from '../../ui/Icon.jsx';
import { safeUrl } from '../../../lib/safeUrl.js';

  const { useState } = React;

  /* ── inline: code → links → bold → italic ── */
  function renderInline(text, kp) {
    if (text == null) return null;
    const out = [];
    // 1) код-спаны `...`
    const parts = String(text).split(/(`[^`]+`)/g);
    parts.forEach((part, i) => {
      if (/^`[^`]+`$/.test(part)) {
        out.push(<code key={kp + 'c' + i} className="md-code-inline">{part.slice(1, -1)}</code>);
        return;
      }
      // 2) ссылки [t](u) — режем и собираем
      let rest = part, idx = 0;
      const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
      let m, last = 0;
      const seg = [];
      while ((m = linkRe.exec(rest)) !== null) {
        if (m.index > last) seg.push({ t: rest.slice(last, m.index) });
        seg.push({ link: { label: m[1], url: m[2] } });
        last = m.index + m[0].length;
      }
      if (last < rest.length) seg.push({ t: rest.slice(last) });
      if (!seg.length) seg.push({ t: rest });
      seg.forEach((s, j) => {
        if (s.link) {
          // URL приходит из недоверенного контента (LLM/файлы) — пропускаем через белый
          // список схем; небезопасные (javascript:/data:/vbscript:) рисуем как обычный текст
          const href = safeUrl(s.link.url);
          if (href) {
            out.push(<a key={kp + 'l' + i + '_' + j} href={href} target="_blank" rel="noopener noreferrer" className="md-link">{s.link.label}</a>);
          } else {
            out.push(...renderEmphasis(s.link.label, kp + 'le' + i + '_' + j));
          }
        } else {
          out.push(...renderEmphasis(s.t, kp + 'e' + i + '_' + j));
        }
      });
    });
    return out;
  }

  // **bold** и *italic* / _italic_
  function renderEmphasis(text, kp) {
    const nodes = [];
    const re = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g;
    let last = 0, m, k = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) nodes.push(text.slice(last, m.index));
      const tok = m[0];
      if (/^(\*\*|__)/.test(tok)) nodes.push(<strong key={kp + 'b' + k}>{tok.slice(2, -2)}</strong>);
      else nodes.push(<em key={kp + 'i' + k}>{tok.slice(1, -1)}</em>);
      last = m.index + m[0].length; k++;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return nodes;
  }

  function CodeBlock({ code }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
      try { navigator.clipboard.writeText(code); } catch (e) {}
      setCopied(true); setTimeout(() => setCopied(false), 1400);
    };
    return (
      <div className="md-pre-wrap">
        <button className="md-pre-copy" onClick={copy} aria-label="Копировать код">
          <Icon name={copied ? 'check' : 'copy'} size={13} /> {copied ? 'Скопировано' : 'Копировать'}
        </button>
        <pre className="md-pre"><code>{code}</code></pre>
      </div>
    );
  }

  function Table({ head, rows, kp }) {
    return (
      <div className="md-table-wrap">
        <table className="md-table">
          <thead><tr>{head.map((c, i) => {
            const n = isNum(c);
            return <th key={i} className={n ? 'num' : ''}>{renderInline(n ? nbspDigits(c) : c, kp + 'th' + i)}</th>;
          })}</tr></thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => {
                const n = isNum(c);
                return <td key={ci} className={n ? 'num' : ''}>{renderInline(n ? nbspDigits(c) : c, kp + 'td' + ri + '_' + ci)}</td>;
              })}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // Разметку выделения снимаем ДО проверки: итоговые строки ассистент пишет
  // жирным («**6,236 кг**»), и без этого они не считались числовыми — теряли
  // выравнивание, а на узком экране рвались посередине числа («6,23 6 кг»).
  export const isNum = (s) => /^[−\-+]?[\d.,\s]+(%|г|кг|₽|ккал)?$/
    .test(String(s).replace(/[*_`]/g, '').trim());

  /* Разряды внутри числа склеиваем неразрывным пробелом: «3 060,00 ₽» на узком
     экране рвалось по разряду и читалось как два числа. Пробел ПЕРЕД единицей
     оставляем обычным — пусть «кг»/«₽» уезжает на вторую строку, это не мешает
     читать, а колонку не раздувает (иначе таблица уползает за край экрана). */
  export const nbspDigits = (s) =>
    String(s).replace(/(\d)[    ](?=\d)/g, '$1 ');

  /* ── блочный парсер ── */
  const LI_RE = /^(\s*)(\d+[.)]|[-*])\s+(.*)$/;

  const dedent = (lines) => {
    let min = Infinity;
    lines.forEach((l) => { if (l.trim()) min = Math.min(min, l.match(/^\s*/)[0].length); });
    if (!isFinite(min) || min === 0) return lines;
    return lines.map((l) => (l.trim() ? l.slice(min) : l));
  };

  // список: пункты + continuation-строки (отступ глубже маркера) + вложенные списки в children
  function parseList(lines, i) {
    const first = lines[i].match(LI_RE);
    const baseIndent = first[1].length;
    const ordered = /^\d/.test(first[2]);
    const start = ordered ? (parseInt(first[2], 10) || 1) : 1;
    const items = [];
    let cur = null;
    while (i < lines.length) {
      const line = lines[i];
      const m = line.match(LI_RE);
      if (m && m[1].length <= baseIndent) {
        if (/^\d/.test(m[2]) !== ordered) break; // смена типа маркера — новый список
        if (cur) items.push(cur);
        cur = { head: m[3], children: [] };
        i++; continue;
      }
      if (!line.trim()) {
        // пустая строка: список продолжается, только если дальше отступленное продолжение или пункт того же типа
        const nxt = lines[i + 1];
        if (nxt == null) break;
        if (nxt.trim() && nxt.match(/^\s*/)[0].length > baseIndent) { if (cur) cur.children.push(''); i++; continue; }
        const nm = nxt.match(LI_RE);
        if (nm && nm[1].length <= baseIndent && /^\d/.test(nm[2]) === ordered) { i++; continue; }
        break;
      }
      if (line.match(/^\s*/)[0].length > baseIndent && cur) { cur.children.push(line); i++; continue; }
      break; // непустая строка на базовом уровне без маркера — конец списка
    }
    if (cur) items.push(cur);
    return { block: { t: ordered ? 'ol' : 'ul', start, items }, next: i };
  }

  function parseBlocks(lines) {
    const blocks = [];
    let i = 0;
    const splitRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((s) => s.trim());

    while (i < lines.length) {
      let line = lines[i];

      // код-блок ```
      if (/^```/.test(line.trim())) {
        const buf = []; i++;
        while (i < lines.length && !/^```/.test(lines[i].trim())) { buf.push(lines[i]); i++; }
        i++; // закрывающий ```
        blocks.push({ t: 'code', code: buf.join('\n') });
        continue;
      }
      // пустая строка
      if (!line.trim()) { i++; continue; }
      // hr
      if (/^(---|\*\*\*|___)\s*$/.test(line.trim())) { blocks.push({ t: 'hr' }); i++; continue; }
      // заголовок
      const h = line.match(/^(#{1,6})\s+(.*)$/);
      if (h) { blocks.push({ t: 'h', level: h[1].length, text: h[2] }); i++; continue; }
      // таблица: строка с | + следующая строка-разделитель ---|---
      if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:-]*-[\s:|-]*$/.test(lines[i + 1])) {
        const head = splitRow(line); i += 2;
        const rows = [];
        while (i < lines.length && lines[i].includes('|') && lines[i].trim()) { rows.push(splitRow(lines[i])); i++; }
        blocks.push({ t: 'table', head, rows });
        continue;
      }
      // цитата
      if (/^>\s?/.test(line)) {
        const buf = [];
        while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, '')); i++; }
        blocks.push({ t: 'quote', text: buf.join(' ') });
        continue;
      }
      // список (ul/ol с continuation-строками и вложенностью)
      if (LI_RE.test(line)) {
        const r = parseList(lines, i);
        blocks.push(r.block); i = r.next;
        continue;
      }
      // абзац (собираем подряд идущие непустые не-блочные строки)
      const para = [line]; i++;
      while (i < lines.length && lines[i].trim()
             && !/^(#{1,6}\s|>\s?|```)/.test(lines[i]) && !LI_RE.test(lines[i])
             && !(lines[i].includes('|') && /^\s*\|?[\s:-]*-[\s:|-]*$/.test(lines[i + 1] || ''))) {
        para.push(lines[i]); i++;
      }
      blocks.push({ t: 'p', text: para.join(' ') });
    }
    return blocks;
  }

  function renderBlocks(blocks, kpRoot) {
    return blocks.map((b, bi) => {
      const key = kpRoot + bi, kp = key + '_';
      if (b.t === 'h') { const H = 'h' + Math.min(6, Math.max(3, b.level + 2)); return React.createElement(H, { key, className: 'md-h md-h' + b.level }, renderInline(b.text, kp)); }
      if (b.t === 'p') return <p key={key} className="md-p">{renderInline(b.text, kp)}</p>;
      if (b.t === 'ul' || b.t === 'ol') {
        const Tag = b.t;
        return (
          <Tag key={key} className={'md-' + b.t} start={b.t === 'ol' && b.start !== 1 ? b.start : undefined}>
            {b.items.map((it, j) => (
              <li key={j}>
                {renderInline(it.head, kp + j)}
                {it.children.length ? renderBlocks(parseBlocks(dedent(it.children)), kp + j + 'c') : null}
              </li>
            ))}
          </Tag>
        );
      }
      if (b.t === 'quote') return <blockquote key={key} className="md-quote">{renderInline(b.text, kp)}</blockquote>;
      if (b.t === 'code') return <CodeBlock key={key} code={b.code} />;
      if (b.t === 'table') return <Table key={key} head={b.head} rows={b.rows} kp={kp} />;
      if (b.t === 'hr') return <hr key={key} className="md-hr" />;
      return null;
    });
  }

  function Markdown({ source }) {
    const lines = String(source || '').replace(/\r/g, '').split('\n');
    return <div className="md">{renderBlocks(parseBlocks(lines), 'b')}</div>;
  }

export { Markdown as ChatMarkdown };
